import baseService from './support/base-service';
import * as Mutations from '../graphql/mutations';
import * as Subscriptions from '../graphql/subscriptions';
import gql from 'graphql-tag';
import awsVars from '../config/aws-exports';
import Auth from '@aws-amplify/auth';

// Verbose signaling/track logging is gated behind this flag.
// Errors and warnings are always logged.
const WEBRTC_DEBUG = process.env.NODE_ENV !== 'production';
function debugLog() {
	if (WEBRTC_DEBUG) {
		console.log.apply(console, arguments);
	}
}

// ICE servers configuration with STUN and TURN for better connectivity
// TURN servers are critical for production environments with restrictive firewalls
//
// AUTO-DETECTION:
// ===============
// This service automatically detects and uses Twilio TURN servers if available.
// If the getTwilioToken Lambda is deployed, it will be auto-discovered and used.
// No configuration needed - it just works!
//
// FALLBACK:
// If Twilio Lambda is not available, falls back to free STUN servers.
// Free STUN works on most networks but may fail on restrictive firewalls.
//
// Default ICE servers (STUN only, used as fallback)
const DEFAULT_ICE_SERVERS = [
	// Twilio's public STUN server (free, no auth)
	{ urls: 'stun:global.stun.twilio.com:3478' },
	// Google's public STUN servers (fallback)
	{ urls: 'stun:stun.l.google.com:19302' },
	{ urls: 'stun:stun1.l.google.com:19302' }
];

/**
 * Auto-detect Twilio token endpoint from AppSync GraphQL endpoint
 * AppSync and Lambda are typically on the same API Gateway
 * Lazy-loaded to avoid webpack module resolution issues
 */
function getTwilioTokenEndpoint() {
	// Check if explicitly configured (backwards compatibility)
	if (process.env.VUE_APP_twilio_token_endpoint) {
		return process.env.VUE_APP_twilio_token_endpoint;
	}
	
	// Get AWS config (lazy load)
	const AWSConfig = awsVars.default(process.env);

	// Use the REST API Gateway endpoint, which hosts the /getTwilioToken route
	// (e.g. https://xxxxx.execute-api.us-east-1.amazonaws.com/prod/getTwilioToken)
	const restApiEndpoint = AWSConfig && AWSConfig.aws_rest_api_endpoint;
	if (restApiEndpoint) {
		return restApiEndpoint.replace(/\/?$/, '/') + 'getTwilioToken';
	}

	// If no REST endpoint, try relative path (works if hosted on same domain)
	return '/getTwilioToken';
}

// Cache for Twilio availability check
let twilioAvailabilityChecked = false;
let twilioAvailable = false;
let cachedTwilioEndpoint = null;

class WebRTCService extends baseService {
	constructor() {
		super();
		this.peerConnections = new Map();
		this.pendingOffers = new Map(); // Queue offers that arrive before local stream is ready
		this.pendingIceCandidates = new Map(); // Queue ICE candidates that arrive before the peer connection / remote description is ready
		this.reconnectionCooldown = new Map(); // Track failed peers to prevent rapid reconnection (userId -> timestamp)
		this.connectionGraceTimers = new Map(); // peerId -> timeout handle for disconnected/failed recovery grace period
		this.peerStreamMeta = new Map(); // userId -> { cameraStreamId, screenStreamId } announced in offer/answer signals
		this.peerScreenStreamIds = new Map(); // userId -> stream id of the screen stream currently delivered to the UI
		this.peerCameraStreamIds = new Map(); // userId -> stream id of the camera stream currently delivered to the UI
		this.isLocalStreamReady = false;
		this.localStream = null;
		this.screenStream = null;
		this.vaultId = null;
		this.userId = null;
		this.sessionId = null; // Unique session ID for this connection (supports multiple devices per user)
		this.isScreenSharing = false;
		this.presenceHeartbeatInterval = null;
		this.visibilityHandler = null; // Heartbeat on tab visibility change (background tabs throttle timers)
		this.signalSubscription = null;
		this.onRemoteStreamCallback = null;
		this.onRemoteStreamRemovedCallback = null;
		this.onConnectionStateCallback = null;
		this.onScreenShareStoppedCallback = null; // Callback for when screen share is stopped
		this.onPresentMessageCallback = null; // Callback for present-sync data channel messages
		this.onPresentChannelOpenCallback = null; // Callback when a present-sync channel opens (late-joiner state push)
		this.twilioIceServers = null; // Cache Twilio ICE servers
		this.twilioTokenExpiry = null; // Track when Twilio token expires
		this.turnActive = false; // Whether TURN relay credentials are in use (vs STUN-only fallback)
	}

	async initialize(vaultId, userId, callbacks) {
		this.vaultId = vaultId;
		this.userId = userId;
		// Generate unique session ID for this connection (supports multiple devices per user)
		this.sessionId = `${userId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
		this.onRemoteStreamCallback = callbacks.onRemoteStream;
		this.onRemoteStreamRemovedCallback = callbacks.onRemoteStreamRemoved;
		this.onConnectionStateCallback = callbacks.onConnectionState;
		this.onScreenShareStoppedCallback = callbacks.onScreenShareStopped;
		this.onPresentMessageCallback = callbacks.onPresentMessage || null;
		this.onPresentChannelOpenCallback = callbacks.onPresentChannelOpen || null;

		console.log('🚀 Initializing WebRTC service...');
		console.log(`   Vault ID: ${vaultId}`);
		console.log(`   User ID: ${userId}`);
		console.log(`   Session ID: ${this.sessionId}`);

		// Log browser capabilities
		this.logBrowserCapabilities();

		// Start presence heartbeat
		await this.updatePresence('online');
		this.startPresenceHeartbeat();

		// Subscribe to WebRTC signals
		await this.subscribeToSignals();
	}

	logBrowserCapabilities() {
		console.log('🔍 Browser WebRTC Capabilities:');
		console.log(`   - getUserMedia: ${!!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)}`);
		console.log(`   - RTCPeerConnection: ${!!window.RTCPeerConnection}`);
		console.log(`   - getDisplayMedia: ${!!(navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia)}`);
		console.log(`   - User Agent: ${navigator.userAgent}`);
		console.log(`   - Platform: ${navigator.platform}`);
	}
	
	/**
	 * Get the Cognito ID token for calling the (authenticated) REST API.
	 * Returns null if no session is available.
	 */
	async getAuthToken() {
		try {
			const session = await Auth.currentSession();
			return session.getIdToken().getJwtToken();
		} catch (err) {
			console.warn('⚠️ Could not get auth token for TURN request:', err && err.message);
			return null;
		}
	}

	/**
	 * Check if Twilio token endpoint is available
	 * This is called once per session to auto-detect Twilio support
	 */
	async checkTwilioAvailability() {
		twilioAvailabilityChecked = true;
		
		try {
			// Get the endpoint (lazy load)
			if (!cachedTwilioEndpoint) {
				cachedTwilioEndpoint = getTwilioTokenEndpoint();
			}
			
			debugLog('🔍 Auto-detecting Twilio TURN server support...');
			debugLog(`   Trying endpoint: ${cachedTwilioEndpoint}`);
			
			const controller = new AbortController();
			// 8 second timeout: first invocation is a Lambda cold start that also
			// fetches the Twilio secret from Secrets Manager, which can exceed 3s
			const timeoutId = setTimeout(() => controller.abort(), 8000);
			
			const authToken = await this.getAuthToken();
			const headers = authToken ? { 'Authorization': authToken } : {};
			
			const response = await fetch(cachedTwilioEndpoint, {
				method: 'GET',
				headers: headers,
				signal: controller.signal
			});
			
			clearTimeout(timeoutId);
			
			if (response.ok) {
				const data = await response.json();
				if (data.iceServers && Array.isArray(data.iceServers)) {
					twilioAvailable = true;
					debugLog('✅ Twilio TURN servers detected and enabled');
					return;
				}
			}
			
			console.warn(`⚠️ TURN endpoint returned ${response.status} - falling back to STUN only. Calls may fail behind restrictive firewalls.`);
			twilioAvailable = false;
			
		} catch (error) {
			console.warn('⚠️ TURN endpoint unreachable - falling back to STUN only. Calls may fail behind restrictive firewalls.');
			twilioAvailable = false;
		}
	}

	/**
	 * Get ICE servers configuration (STUN/TURN)
	 * Auto-detects Twilio availability and falls back to free STUN servers
	 */
	async getIceServers() {
		// If we haven't checked Twilio availability yet, try it once
		if (!twilioAvailabilityChecked) {
			await this.checkTwilioAvailability();
		}

		// If Twilio is not available, use default STUN servers
		if (!twilioAvailable) {
			this.turnActive = false;
			return DEFAULT_ICE_SERVERS;
		}

		// Check if we have a valid cached token
		if (this.twilioIceServers && this.twilioTokenExpiry && Date.now() < this.twilioTokenExpiry) {
			debugLog('📡 Using cached Twilio ICE servers');
			this.turnActive = true;
			return this.twilioIceServers;
		}

		// Fetch new Twilio token
		try {
			debugLog('📡 Fetching dynamic Twilio ICE servers...');
			
			// Get the endpoint (should be cached by now)
			if (!cachedTwilioEndpoint) {
				cachedTwilioEndpoint = getTwilioTokenEndpoint();
			}
			
			const authToken = await this.getAuthToken();
			const headers = authToken ? { 'Authorization': authToken } : {};
			
			const response = await fetch(cachedTwilioEndpoint, { headers: headers });
			
			if (!response.ok) {
				throw new Error(`Twilio token endpoint returned ${response.status}`);
			}

			const data = await response.json();
			
			if (!data.iceServers || !Array.isArray(data.iceServers)) {
				throw new Error('Invalid Twilio token response format');
			}

			// Cache the token
			this.twilioIceServers = data.iceServers;
			// Refresh 15 minutes before actual expiry for safety margin
			this.twilioTokenExpiry = data.expiresAt 
				? new Date(data.expiresAt).getTime() - (15 * 60 * 1000)
				: Date.now() + (3 * 60 * 60 * 1000); // 3 hours default

			debugLog('✅ Successfully fetched Twilio ICE servers (TURN enabled)');
			this.turnActive = true;
			return this.twilioIceServers;

		} catch (error) {
			console.warn('⚠️ Failed to fetch Twilio ICE servers, falling back to STUN only. Calls may fail behind restrictive firewalls:', error.message);
			
			// Mark Twilio as unavailable so we don't keep trying
			twilioAvailable = false;
			this.turnActive = false;
			
			// Fall back to free STUN servers
			return DEFAULT_ICE_SERVERS;
		}
	}

	async updatePresence(status) {
		// Guard: Don't attempt to update presence if vaultId or sessionId is not set
		if (!this.vaultId || !this.sessionId) {
			console.warn('⚠️ Cannot update presence: vaultId or sessionId not set');
			return;
		}
		
		return this.updatePresenceWithIds(this.vaultId, this.sessionId, status);
	}
	
	async updatePresenceWithIds(vaultId, sessionId, status) {
		try {
			debugLog(`📡 Updating presence to "${status}" for vault:`, vaultId, 'session:', sessionId);
			await this.mutation('updateVaultPresence', {
				vault_id: vaultId,
				session_id: sessionId,
				status: status
			});
		} catch (error) {
			console.error(`❌ Error updating presence (vault: ${vaultId}, session: ${sessionId}, status: ${status}):`, error);
		}
	}

	startPresenceHeartbeat() {
		// Send heartbeat every 30 seconds
		this.presenceHeartbeatInterval = setInterval(() => {
			this.updatePresence('online');
		}, 30000);
		
		// Background tabs throttle timers (Chrome: >=60s), which can exceed the
		// server's stale threshold. Send an immediate heartbeat when the tab
		// becomes visible again, and one final heartbeat when it hides.
		this.visibilityHandler = () => {
			if (this.vaultId && this.sessionId) {
				this.updatePresence('online');
			}
		};
		document.addEventListener('visibilitychange', this.visibilityHandler);
	}

	stopPresenceHeartbeat() {
		if (this.presenceHeartbeatInterval) {
			clearInterval(this.presenceHeartbeatInterval);
			this.presenceHeartbeatInterval = null;
		}
		
		if (this.visibilityHandler) {
			document.removeEventListener('visibilitychange', this.visibilityHandler);
			this.visibilityHandler = null;
		}
	}

	async subscribeToSignals() {
		const self = this;
		
		debugLog(`🔔 Subscribing to WebRTC signals for vault: ${this.vaultId}, user: ${this.userId}`);

		try {
			const subscription = await this.appSyncClient.subscribe({
				query: gql(Subscriptions.onWebRTCSignal),
				variables: {
					vault_id: this.vaultId,
					to_user_id: this.userId
				},
				fetchPolicy: 'network-only' // Changed from no-cache to network-only
			});

		this.signalSubscription = subscription.subscribe({
			next: (data) => {
				// Check both possible structures: data.data or direct data property depending on client version
				const signalData = data.data ? data.data.onWebRTCSignal : data.onWebRTCSignal;
				
				if (signalData) {
					self.handleIncomingSignal(signalData).catch(err => console.error('Error handling signal:', err));
				} else if (data && data.value && data.value.data && data.value.data.onWebRTCSignal) {
					// Sometimes AppSync client wraps data in a value property
					const deepSignal = data.value.data.onWebRTCSignal;
					self.handleIncomingSignal(deepSignal).catch(err => console.error('Error handling signal:', err));
				} else {
					console.warn('⚠️ Received subscription data but no signal found in known paths. Data keys:', Object.keys(data || {}));
				}
			},
			error: (error) => {
				console.error('❌ Signal subscription error:', error);
			}
		});
			
			debugLog('✅ Successfully subscribed to WebRTC signals');
		} catch (error) {
			console.error('❌ Error subscribing to signals:', error);
		}
	}

	async handleIncomingSignal(signal) {
		const { id, from_user_id, from_user_name, signal_type, signal_data } = signal;

		debugLog(`📥 Received signal notification: ${signal_type} from ${from_user_id}`);

		let resolvedSignalType = signal_type;
		let resolvedSignalData = null;
		let fromSessionId = null;

		// CAPTURE THE SIGNAL ID IMMEDIATELY - don't let subsequent subscriptions overwrite it
		const currentSignalId = id || from_user_name || null;
		
		// Fast path: payload delivered inline on the subscription notification
		if (signal_data && typeof signal_data === 'string') {
			const trimmed = signal_data.trim();
			if (trimmed.charAt(0) === '{' || trimmed.charAt(0) === '[') {
				try {
					resolvedSignalData = JSON.parse(trimmed);
					fromSessionId = resolvedSignalData.fromSessionId;
				} catch (parseError) {
					console.warn('⚠️ Failed to parse inline signal payload, will fetch via ID:', parseError.message);
				}
			}
		}
		
		// Fallback: fetch the stored payload from DynamoDB by signal ID.
		// Only delay BETWEEN retries (DynamoDB eventual consistency) - the first
		// attempt happens immediately to keep signaling latency low.
		if (!resolvedSignalData) {
			if (!currentSignalId) {
				console.error('❌ No signal ID or payload received (checked id, from_user_name, signal_data)');
				return;
			}

			debugLog(`🔍 Fetching signal data for ID: ${currentSignalId}`);

			try {
				let result = null;
				let retries = 0;
				const maxRetries = 6;
				const retryDelay = 500;

				while (retries < maxRetries && !result) {
					try {
						result = await this.query('getWebRTCSignal', {
							signal_id: currentSignalId
						});

						if (result && result.data && result.data.getWebRTCSignal) {
							break;
						} else if (result && result.id && result.signal_type) {
							// Handle case where signal data is returned directly
							result = { data: { getWebRTCSignal: result } };
							break;
						} else {
							result = null;
						}
					} catch (queryError) {
						console.error(`❌ Query error fetching signal (attempt ${retries + 1}/${maxRetries}):`, queryError);
					}
					
					retries++;
					if (retries < maxRetries) {
						await new Promise(resolve => setTimeout(resolve, retryDelay));
					}
				}

				if (!result || !result.data || !result.data.getWebRTCSignal) {
					console.error('❌ Signal not found after all retries:', currentSignalId);
					return;
				}

				const fullSignal = result.data.getWebRTCSignal;
				resolvedSignalType = fullSignal.signal_type || resolvedSignalType;
				resolvedSignalData = JSON.parse(fullSignal.signal_data);
				fromSessionId = resolvedSignalData.fromSessionId;
				debugLog(`✅ Retrieved full signal data, type: ${resolvedSignalType}`);
			} catch (error) {
				console.error('Error fetching signal payload:', error);
				return;
			}
		}
		
		// Ignore signals from our own other sessions (same user, different device)
		if (fromSessionId && fromSessionId === this.sessionId) {
			debugLog(`🔇 Ignoring signal from own session: ${fromSessionId}`);
			return;
		}
		
		// Use session ID as peer ID if available, otherwise fall back to user ID
		const peerId = fromSessionId || from_user_id;
		const peerUserId = fromSessionId ? fromSessionId.split('_')[0] : from_user_id;
		
		// Offers/answers announce which remote stream is the camera and which is
		// the screen share - record it before processing the SDP so ontrack can
		// classify tracks deterministically
		if (resolvedSignalData && resolvedSignalData.streamMeta) {
			this.updatePeerStreamMeta(peerUserId, peerId, resolvedSignalData.streamMeta);
		}
		
		try {
			switch (resolvedSignalType) {
				case 'offer':
					debugLog('📨 Processing offer from', from_user_id, 'session:', fromSessionId);
					await this.handleOffer(peerId, resolvedSignalData);
					break;
				case 'answer':
					debugLog('📨 Processing answer from', from_user_id, 'session:', fromSessionId);
					await this.handleAnswer(peerId, resolvedSignalData);
					break;
				case 'ice-candidate':
					debugLog('🧊 Processing ICE candidate from', from_user_id, 'session:', fromSessionId);
					await this.handleIceCandidate(peerId, resolvedSignalData);
					break;
				case 'leave':
					debugLog('👋 Peer leaving:', from_user_id, 'session:', fromSessionId);
					this.handlePeerLeave(peerId);
					break;
				default:
					console.warn('⚠️ Unknown signal type received:', resolvedSignalType);
			}
		} catch (error) {
			console.error('Error handling signal:', error);
		}
	}

	/**
	 * Record the camera/screen stream ids a peer announced with their
	 * offer/answer. If a previously announced screen stream is no longer
	 * listed, the peer stopped sharing - clear it in the UI atomically with
	 * the renegotiation that removes the track.
	 */
	updatePeerStreamMeta(peerUserId, peerId, meta) {
		const previous = this.peerStreamMeta.get(peerUserId);
		this.peerStreamMeta.set(peerUserId, meta);
		
		const previousScreenId = previous ? previous.screenStreamId : null;
		const currentScreenId = meta ? meta.screenStreamId : null;
		
		if (previousScreenId && !currentScreenId && this.peerScreenStreamIds.has(peerUserId)) {
			debugLog(`🧹 Peer ${peerUserId} stopped screen sharing (announced via signal) - clearing screen stream`);
			this.peerScreenStreamIds.delete(peerUserId);
			if (this.onRemoteStreamCallback) {
				this.onRemoteStreamCallback(peerId, null, true);
			}
		}
	}

	async sendSignal(toUserId, signalType, signalData) {
		return this.sendSignalWithIds(this.vaultId, this.sessionId, this.userId, toUserId, signalType, signalData);
	}
	
	async sendSignalWithIds(vaultId, sessionId, fromUserId, toUserId, signalType, signalData) {
		try {
			debugLog(`📤 Sending ${signalType} to ${toUserId}`);
			
			// Include session information in the signal data
			const enrichedData = {
				...signalData,
				fromSessionId: sessionId,
				fromUserId: fromUserId
			};
			
			const payload = JSON.stringify(enrichedData);
			
			await this.mutation('sendWebRTCSignal', {
				vault_id: vaultId,
				to_user_id: toUserId,
				signal_type: signalType,
				signal_data: payload
			});
			debugLog(`✅ Successfully sent ${signalType} to ${toUserId}`);
		} catch (error) {
			console.error(`❌ Error sending ${signalType} to ${toUserId}:`, error);
		}
	}

	/**
	 * Build the payload for an offer/answer signal from the current local
	 * description, announcing which of our streams is camera vs screen so the
	 * remote peer can classify incoming tracks deterministically.
	 */
	buildSessionDescriptionPayload(pc) {
		return {
			type: pc.localDescription.type,
			sdp: pc.localDescription.sdp,
			streamMeta: {
				cameraStreamId: this.localStream ? this.localStream.id : null,
				screenStreamId: (this.isScreenSharing && this.screenStream) ? this.screenStream.id : null
			}
		};
	}

	async createPeerConnection(peerId, userId) {
		// Auto-detect and get ICE servers (Twilio TURN if available, otherwise free STUN)
		const iceServers = await this.getIceServers();
		
		debugLog(`🔧 Creating peer connection for ${peerId} with ${iceServers.length} ICE server(s), TURN active: ${this.turnActive}`);
		
		const pc = new RTCPeerConnection({
			iceServers: iceServers,
			iceCandidatePoolSize: 10,
			bundlePolicy: 'max-bundle',
			rtcpMuxPolicy: 'require'
		});
		
		// Store userId with the peer connection for signaling
		pc._userId = userId;
		pc._remoteSessionId = null; // Set when we learn which remote session this connection belongs to
		pc._makingOffer = false; // Perfect negotiation state
		pc._ignoreOffer = false; // Perfect negotiation state
		pc._iceRestartAttempted = false;
		
		// Present-sync data channel for in-call co-viewing (file open, page/
		// scroll/zoom, cursors, annotations). Negotiated with a fixed id so
		// both sides create it symmetrically - no ondatachannel race and no
		// extra renegotiation. Attached to the pc so it survives the
		// userId -> sessionId re-keying of peerConnections.
		try {
			const presentChannel = pc.createDataChannel('present-sync', { negotiated: true, id: 0 });
			this.setupPresentChannel(pc, presentChannel, peerId, userId);
		} catch (error) {
			console.warn(`⚠️ Could not create present-sync data channel for ${peerId}:`, error && error.message);
		}
		
		// Add local stream tracks
		if (this.localStream) {
			// IMPORTANT: Add tracks in consistent order (audio first, then video) to prevent SDP m-line ordering issues
			const audioTracks = this.localStream.getAudioTracks();
			const videoTracks = this.localStream.getVideoTracks();
			const tracks = [...audioTracks, ...videoTracks]; // Consistent order: audio then video
			
			tracks.forEach(track => {
				if (track.readyState === 'live') {
					debugLog(`✅ Adding ${track.kind} track to peer ${peerId} (${track.id})`);
					pc.addTrack(track, this.localStream);
				} else {
					console.warn(`⚠️ Track ${track.kind} is not live (${track.readyState}) - not adding to peer ${peerId}`);
				}
			});
		} else {
			// Listener mode (no camera/microphone): add receive-only transceivers
			// so negotiation still happens and we can receive remote audio/video
			debugLog(`👂 No local stream - creating receive-only connection for ${peerId}`);
			pc.addTransceiver('audio', { direction: 'recvonly' });
			pc.addTransceiver('video', { direction: 'recvonly' });
		}
		
		// If we're already sharing our screen, include the screen track so
		// peers joining mid-share receive it too
		if (this.isScreenSharing && this.screenStream) {
			const screenTrack = this.screenStream.getVideoTracks()[0];
			if (screenTrack && screenTrack.readyState === 'live') {
				debugLog(`🖥️ Adding active screen share track for peer ${peerId} (joined mid-share)`);
				pc.addTrack(screenTrack, this.screenStream);
			}
		}

		// Handle ICE candidates
		pc.onicecandidate = (event) => {
			if (event.candidate) {
				// Send to userId (not sessionId) so all their sessions receive it
				this.sendSignal(userId, 'ice-candidate', event.candidate.toJSON());
			} else {
				debugLog(`✅ ICE gathering complete for peer ${peerId}`);
			}
		};

		// Handle remote tracks. Camera and screen arrive as distinct streams:
		// the sender's stream ids propagate via SDP msid (event.streams[0]) and
		// are matched against the stream metadata announced in offer/answer signals.
		pc.ontrack = (event) => {
			const track = event.track;
			const stream = (event.streams && event.streams.length > 0)
				? event.streams[0]
				: new MediaStream([track]); // Defensive fallback - senders always pass a stream
			
			debugLog(`🎥 Received ${track.kind} track from peer ${peerId} (stream ${stream.id})`);
			
			if (!this.onRemoteStreamCallback) {
				console.warn('⚠️ ontrack event but no callback registered');
				return;
			}
			
			let isScreenTrack;
			if (track.kind === 'video') {
				isScreenTrack = this.isRemoteScreenStream(userId, stream);
				if (isScreenTrack) {
					this.peerScreenStreamIds.set(userId, stream.id);
				} else {
					this.peerCameraStreamIds.set(userId, stream.id);
				}
			} else {
				// Audio is classified by the stream it arrives in
				isScreenTrack = this.peerScreenStreamIds.get(userId) === stream.id;
			}
			
			debugLog(`   ⭐ Track classified as ${isScreenTrack ? '🖥️ SCREEN' : '📹 CAMERA'}`);
			
			// Backup for the signal-based clear: if the screen track ends, clear the screen stream in the UI
			track.onended = () => {
				debugLog(`🛑 ${isScreenTrack ? 'Screen' : 'Camera'} ${track.kind} track ended for peer ${peerId}`);
				if (isScreenTrack && track.kind === 'video' && this.peerScreenStreamIds.get(userId) === stream.id) {
					this.peerScreenStreamIds.delete(userId);
					if (this.onRemoteStreamCallback) {
						this.onRemoteStreamCallback(peerId, null, true); // null stream + isScreenTrack means "remove screen"
					}
				}
			};
			
			this.onRemoteStreamCallback(peerId, stream, isScreenTrack);
		};

		// Handle connection state changes with recovery instead of immediate teardown:
		// - 'disconnected' is usually transient; give it a grace period
		// - 'failed' gets one ICE restart attempt before closing
		pc.onconnectionstatechange = () => {
			const state = pc.connectionState;
			debugLog(`🔗 Peer ${peerId} connection state: ${state}`);
			if (this.onConnectionStateCallback) {
				this.onConnectionStateCallback(peerId, state);
			}
			
			const peerUserId = peerId.includes('_') ? peerId.split('_')[0] : peerId;
			
			if (state === 'connected') {
				debugLog(`✅ Successfully connected to peer ${peerId}`);
				this.clearConnectionGraceTimer(peerId);
				pc._iceRestartAttempted = false;
				this.reconnectionCooldown.delete(peerUserId);
			} else if (state === 'disconnected') {
				console.warn(`⚠️ Connection disconnected for peer ${peerId} - waiting up to 10s for recovery`);
				this.scheduleConnectionGraceTimer(peerId, pc);
			} else if (state === 'failed') {
				if (!pc._iceRestartAttempted && typeof pc.restartIce === 'function') {
					console.warn(`⚠️ Connection failed for peer ${peerId} - attempting ICE restart`);
					pc._iceRestartAttempted = true;
					pc.restartIce(); // Triggers onnegotiationneeded -> new offer with fresh candidates
					this.scheduleConnectionGraceTimer(peerId, pc);
				} else {
					console.error(`❌ Connection failed for peer ${peerId} (ICE restart did not help) - closing`);
					this.teardownFailedPeer(peerId);
				}
			} else if (state === 'closed') {
				this.clearConnectionGraceTimer(peerId);
			}
		};

		// Handle ICE connection state changes for better debugging
		pc.oniceconnectionstatechange = () => {
			debugLog(`🧊 Peer ${peerId} ICE connection state: ${pc.iceConnectionState}`);
			if (pc.iceConnectionState === 'failed') {
				console.error(`❌ ICE connection failed for peer ${peerId}.${this.turnActive ? '' : ' TURN is NOT active - this network may require a TURN relay.'}`);
			}
		};

		// Log signaling state changes
		pc.onsignalingstatechange = () => {
			debugLog(`📡 Peer ${peerId} signaling state: ${pc.signalingState}`);
		};

		// Perfect negotiation: whenever local changes require (re)negotiation
		// (tracks added/removed, ICE restart), send an offer. Offer collisions
		// (glare) are resolved in handleOffer via the politeness rule.
		pc.onnegotiationneeded = async () => {
			try {
				debugLog(`🔄 Negotiation needed for peer ${peerId}`);
				pc._makingOffer = true;
				await pc.setLocalDescription();
				await this.sendSignal(userId, 'offer', this.buildSessionDescriptionPayload(pc));
			} catch (error) {
				console.error(`❌ Error negotiating with peer ${peerId}:`, error.message);
			} finally {
				pc._makingOffer = false;
			}
		};

		this.peerConnections.set(peerId, pc);
		return pc;
	}

	/**
	 * Classify a remote video stream as screen share or camera.
	 * Prefers the stream metadata announced with the peer's offer/answer;
	 * falls back to "a second, different video stream is a screen share".
	 */
	isRemoteScreenStream(userId, stream) {
		const meta = this.peerStreamMeta.get(userId);
		if (meta) {
			if (meta.screenStreamId && stream.id === meta.screenStreamId) {
				return true;
			}
			if (meta.cameraStreamId && stream.id === meta.cameraStreamId) {
				return false;
			}
		}
		
		const knownScreenId = this.peerScreenStreamIds.get(userId);
		if (knownScreenId && knownScreenId === stream.id) {
			return true;
		}
		
		const knownCameraId = this.peerCameraStreamIds.get(userId);
		return !!(knownCameraId && knownCameraId !== stream.id);
	}

	scheduleConnectionGraceTimer(peerId, pc) {
		if (this.connectionGraceTimers.has(peerId)) {
			return;
		}
		const timer = setTimeout(() => {
			this.connectionGraceTimers.delete(peerId);
			if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') {
				console.error(`❌ Peer ${peerId} did not recover (${pc.connectionState}) - closing connection`);
				this.teardownFailedPeer(peerId);
			}
		}, 10000);
		this.connectionGraceTimers.set(peerId, timer);
	}

	clearConnectionGraceTimer(peerId) {
		const timer = this.connectionGraceTimers.get(peerId);
		if (timer) {
			clearTimeout(timer);
			this.connectionGraceTimers.delete(peerId);
		}
	}

	teardownFailedPeer(peerId) {
		// Set reconnection cooldown to prevent rapid reconnection attempts
		const peerUserId = peerId.includes('_') ? peerId.split('_')[0] : peerId;
		this.reconnectionCooldown.set(peerUserId, Date.now());
		this.handlePeerLeave(peerId);
	}

	async getMediaDevices() {
		try {
			const devices = await navigator.mediaDevices.enumerateDevices();
			return {
				audio: devices.filter(d => d.kind === 'audioinput'),
				video: devices.filter(d => d.kind === 'videoinput'),
				audioOutput: devices.filter(d => d.kind === 'audiooutput')
			};
		} catch (error) {
			console.error('Error listing media devices:', error);
			return { audio: [], video: [], audioOutput: [] };
		}
	}

	async startLocalStream(audioDeviceId = null, videoDeviceId = null, triedDevices = { audio: [], video: [] }) {
		try {
			const constraints = {
				audio: audioDeviceId ? { deviceId: { exact: audioDeviceId } } : true,
				video: videoDeviceId ? 
					{ deviceId: { exact: videoDeviceId }, width: 1280, height: 720 } : 
					{ width: 1280, height: 720 }
			};
			
		console.log('Requesting media devices with constraints:', constraints);
		this.localStream = await navigator.mediaDevices.getUserMedia(constraints);
		console.log('Got media stream:', this.localStream);
		console.log('Stream tracks:', this.localStream.getTracks().map(t => ({ kind: t.kind, label: t.label, enabled: t.enabled })));
		
		// Mark local stream as ready and process any queued offers
		this.isLocalStreamReady = true;
		console.log(`✅ Local stream ready! Processing ${this.pendingOffers.size} queued offer(s)...`);
		await this.processPendingOffers();
		
		return this.localStream;
		} catch (error) {
			console.error('Error accessing media devices:', error);
			console.error('Error name:', error.name);
			console.error('Error message:', error.message);
			
			// If device is in use and no explicit device was specified, try the next available device
			if (error.name === 'NotReadableError' || error.message.includes('in use')) {
				const devices = await this.getMediaDevices();
				
				// Try next audio device if audio failed and no explicit audio device was specified
				if (!audioDeviceId && devices.audio && devices.audio.length > 0) {
					const availableAudioDevices = devices.audio.filter(d => !triedDevices.audio.includes(d.deviceId));
					if (availableAudioDevices.length > 0) {
						const nextAudioDevice = availableAudioDevices[0];
						console.log(`🔄 Audio device in use, trying next: ${nextAudioDevice.label}`);
						triedDevices.audio.push(nextAudioDevice.deviceId);
						return this.startLocalStream(nextAudioDevice.deviceId, videoDeviceId, triedDevices);
					}
				}
				
				// Try next video device if video failed and no explicit video device was specified
				if (!videoDeviceId && devices.video && devices.video.length > 0) {
					const availableVideoDevices = devices.video.filter(d => !triedDevices.video.includes(d.deviceId));
					if (availableVideoDevices.length > 0) {
						const nextVideoDevice = availableVideoDevices[0];
						console.log(`🔄 Video device in use, trying next: ${nextVideoDevice.label}`);
						triedDevices.video.push(nextVideoDevice.deviceId);
						return this.startLocalStream(audioDeviceId, nextVideoDevice.deviceId, triedDevices);
					}
				}
			}
			
		// If explicit devices were specified and failed, re-throw the original error
		// so the caller can try other device combinations
		if (audioDeviceId || videoDeviceId) {
			// Keep the original error name so fallback logic can detect it
			const deviceError = new Error(error.message);
			deviceError.name = error.name;
			deviceError.originalError = error;
			throw deviceError;
		}
		
		// Provide user-friendly error messages for non-specific device requests
		let userMessage = 'Failed to access camera/microphone';
		if (error.name === 'NotAllowedError') {
			userMessage = 'Camera/microphone access denied. Please allow access in your browser settings.';
		} else if (error.name === 'NotFoundError') {
			userMessage = 'No camera or microphone found on your device.';
		} else if (error.name === 'NotReadableError') {
			userMessage = 'All available devices are in use by another application.';
		}
		
		const enhancedError = new Error(userMessage);
		enhancedError.name = error.name; // Preserve error name
		enhancedError.originalError = error;
		throw enhancedError;
		}
	}

	async switchMediaDevice(kind, deviceId) {
		try {
			const constraints = kind === 'audio' 
				? { audio: { deviceId: { exact: deviceId } }, video: false }
				: { audio: false, video: { deviceId: { exact: deviceId }, width: 1280, height: 720 } };

			const newStream = await navigator.mediaDevices.getUserMedia(constraints);
			const newTrack = newStream.getTracks()[0];

			if (!this.localStream) {
				this.localStream = newStream;
				return newStream;
			}

			// Replace track in local stream
			const oldTrack = this.localStream.getTracks().find(t => t.kind === (kind === 'audio' ? 'audio' : 'video'));
			if (oldTrack) {
				this.localStream.removeTrack(oldTrack);
				oldTrack.stop();
			}
			this.localStream.addTrack(newTrack);

			// Replace track in peer connections
			// If screen sharing is active, don't replace video track (it's currently the screen track)
			// The new camera track will be used when screen sharing stops
			if (kind === 'audio' || !this.isScreenSharing) {
				this.peerConnections.forEach((pc) => {
					const sender = pc.getSenders().find(s => s.track && s.track.kind === (kind === 'audio' ? 'audio' : 'video'));
					if (sender) {
						sender.replaceTrack(newTrack);
					}
				});
			}

			return this.localStream;
		} catch (error) {
			console.error(`Error switching ${kind} device:`, error);
			throw error;
		}
	}

	async connectToPeer(userId) {
		// Check reconnection cooldown (prevent rapid reconnection after failures)
		const cooldownTime = this.reconnectionCooldown.get(userId);
		if (cooldownTime) {
			const timeSinceFailure = Date.now() - cooldownTime;
			const cooldownPeriod = 5000; // 5 seconds
			if (timeSinceFailure < cooldownPeriod) {
				const remainingTime = Math.ceil((cooldownPeriod - timeSinceFailure) / 1000);
				console.log(`⏱️ Reconnection cooldown active for ${userId}. Wait ${remainingTime} more second(s) before reconnecting.`);
				return; // Skip connection attempt during cooldown
			} else {
				// Cooldown expired, clear it
				console.log(`✅ Reconnection cooldown expired for ${userId}, allowing new connection`);
				this.reconnectionCooldown.delete(userId);
			}
		}
		
		console.log(`Initiating connection to user ${userId}`);
		// For outgoing connections, we don't know their sessionId yet, so we use userId as temporary peerId
		const tempPeerId = userId;
		// This will create the connection and add tracks
		// IMPORTANT: Adding tracks will trigger onnegotiationneeded, which will create and send the offer automatically
		// We do NOT need to manually create an offer here, doing so causes race conditions and m-line errors
		await this.createPeerConnection(tempPeerId, userId);
	}

	async processPendingOffers() {
		if (this.pendingOffers.size === 0) {
			return;
		}
		
		debugLog(`📥 Processing ${this.pendingOffers.size} pending offer(s)...`);
		const offers = Array.from(this.pendingOffers.entries());
		this.pendingOffers.clear();
		
		for (const [sessionId, offer] of offers) {
			debugLog(`   Processing queued offer from ${sessionId}`);
			await this.handleOffer(sessionId, offer);
		}
	}

	/**
	 * Listener mode: mark the service ready without a local stream so queued
	 * offers are processed and outgoing receive-only connections can be made.
	 * Called when getUserMedia fails (no devices / permission denied).
	 */
	async markLocalStreamReady() {
		this.isLocalStreamReady = true;
		debugLog(`👂 Service marked ready without local media (listener mode). Processing ${this.pendingOffers.size} queued offer(s)...`);
		await this.processPendingOffers();
	}

	async handleOffer(sessionId, offer) {
		debugLog(`📨 handleOffer called for session ${sessionId}`);
		
		// If local stream isn't ready yet, queue this offer
		if (!this.isLocalStreamReady) {
			debugLog(`⏸️ Local stream not ready yet, queuing offer from ${sessionId}`);
			this.pendingOffers.set(sessionId, offer);
			return;
		}
		
		// Extract userId from sessionId
		const userId = sessionId.split('_')[0];
		
		// Check for existing connection by sessionId first
		let pc = this.peerConnections.get(sessionId);
		
		// If no connection by sessionId, check if we have one by userId (we initiated it)
		if (!pc) {
			const pcByUserId = this.peerConnections.get(userId);
			if (pcByUserId) {
				if (pcByUserId._remoteSessionId && pcByUserId._remoteSessionId !== sessionId) {
					// Multi-device: this connection already belongs to a different
					// session of the same user - create a separate one below
					debugLog(`Connection for ${userId} already claimed by session ${pcByUserId._remoteSessionId}; creating separate connection for ${sessionId}`);
				} else {
					debugLog(`Moving peer connection from ${userId} to ${sessionId}`);
					pc = pcByUserId;
					pc._remoteSessionId = sessionId;
					this.peerConnections.delete(userId);
					this.peerConnections.set(sessionId, pc);
				}
			}
		}
		
		if (!pc) {
			debugLog(`🆕 Creating new peer connection for ${sessionId}`);
			pc = await this.createPeerConnection(sessionId, userId);
			pc._remoteSessionId = sessionId;
		}
		
		try {
			// Perfect negotiation glare handling: when both sides sent offers
			// simultaneously, the "polite" peer (lower session id) rolls back its
			// own offer and accepts the remote one; the "impolite" peer ignores
			// the remote offer and waits for an answer to its own.
			const polite = this.sessionId < sessionId;
			const offerCollision = pc._makingOffer || pc.signalingState !== 'stable';
			pc._ignoreOffer = !polite && offerCollision;
			
			if (pc._ignoreOffer) {
				debugLog(`⚡ Glare: ignoring offer from ${sessionId} (we are the impolite peer)`);
				return;
			}
			
			// setRemoteDescription performs an implicit rollback of our own
			// pending offer when needed (polite peer in a collision)
			await pc.setRemoteDescription({ type: offer.type, sdp: offer.sdp });
			await pc.setLocalDescription();
			
			debugLog(`Sending answer to user ${userId}`);
			// Send to userId (not sessionId) so all their sessions receive it
			await this.sendSignal(userId, 'answer', this.buildSessionDescriptionPayload(pc));
			
			// Process any queued ICE candidates now that remote description is set
			await this.processPendingIceCandidates(sessionId);
		} catch (error) {
			console.error('❌ Error handling offer:', error);
		}
	}

	async handleAnswer(sessionId, answer) {
		// Try to find peer connection by sessionId first
		let pc = this.peerConnections.get(sessionId);
		
		// If not found by sessionId, try by userId (we initiated the connection)
		if (!pc) {
			const userId = sessionId.split('_')[0];
			const pcByUserId = this.peerConnections.get(userId);
			if (pcByUserId) {
				// Multi-device: ignore answers from a different session of the same
				// user once the connection has been claimed by another session
				if (pcByUserId._remoteSessionId && pcByUserId._remoteSessionId !== sessionId) {
					debugLog(`🔇 Ignoring answer from ${sessionId} - connection already claimed by session ${pcByUserId._remoteSessionId}`);
					return;
				}
				
				// First answer wins: claim the connection for this session
				debugLog(`Moving peer connection from ${userId} to ${sessionId}`);
				pc = pcByUserId;
				pc._remoteSessionId = sessionId;
				this.peerConnections.delete(userId);
				this.peerConnections.set(sessionId, pc);
			}
		}
		
		if (!pc) {
			console.warn(`Received answer from session ${sessionId} but no peer connection exists`);
			return;
		}
		
		try {
			// Only set remote description if we're in have-local-offer state.
			// If we're already stable, this answer is from a stale/superseded offer.
			if (pc.signalingState === 'have-local-offer') {
				await pc.setRemoteDescription({ type: answer.type, sdp: answer.sdp });
				debugLog(`✅ Successfully set remote description (answer) for session ${sessionId}`);
				
				// Process any queued ICE candidates now that remote description is set
				await this.processPendingIceCandidates(sessionId);
			} else {
				debugLog(`⚠️ Ignoring answer from ${sessionId} in state ${pc.signalingState} (likely superseded offer)`);
			}
		} catch (error) {
			console.error('❌ Error handling answer:', error, '- signaling state:', pc.signalingState);
		}
	}

	async handleIceCandidate(sessionId, candidate) {
		// Try to find peer connection by sessionId first
		let pc = this.peerConnections.get(sessionId);
		
		// If not found by sessionId, try by userId (we initiated the connection).
		// Don't re-key here - the answer claims the connection for a session.
		if (!pc) {
			const userId = sessionId.split('_')[0];
			const pcByUserId = this.peerConnections.get(userId);
			if (pcByUserId) {
				// Multi-device: ignore candidates from a different session of the
				// same user once the connection has been claimed
				if (pcByUserId._remoteSessionId && pcByUserId._remoteSessionId !== sessionId) {
					debugLog(`🔇 Ignoring ICE candidate from ${sessionId} - connection claimed by session ${pcByUserId._remoteSessionId}`);
					return;
				}
				pc = pcByUserId;
			}
		}
		
		// Queue candidates that arrive before the peer connection exists or
		// before the remote description is set - they're flushed by
		// processPendingIceCandidates after the offer/answer is applied
		if (!pc || !pc.remoteDescription) {
			debugLog(`⏸️ Queueing ICE candidate from ${sessionId} (${!pc ? 'no peer connection yet' : 'remote description not yet set'})`);
			if (!this.pendingIceCandidates.has(sessionId)) {
				this.pendingIceCandidates.set(sessionId, []);
			}
			this.pendingIceCandidates.get(sessionId).push(candidate);
			return;
		}
		
		try {
			await pc.addIceCandidate(new RTCIceCandidate(candidate));
		} catch (error) {
			// Suppress errors for candidates belonging to an offer we ignored (glare)
			if (!pc._ignoreOffer) {
				console.error('Error adding ICE candidate:', error);
			}
		}
	}
	
	async processPendingIceCandidates(sessionId) {
		const pendingCandidates = this.pendingIceCandidates.get(sessionId);
		if (pendingCandidates && pendingCandidates.length > 0) {
			debugLog(`📥 Processing ${pendingCandidates.length} queued ICE candidate(s) for ${sessionId}`);
			const pc = this.peerConnections.get(sessionId);
			if (pc && pc.remoteDescription) {
				for (const candidate of pendingCandidates) {
					try {
						await pc.addIceCandidate(new RTCIceCandidate(candidate));
					} catch (error) {
						if (!pc._ignoreOffer) {
							console.error('Error adding queued ICE candidate:', error);
						}
					}
				}
			}
			this.pendingIceCandidates.delete(sessionId);
		}
	}

	handlePeerLeave(sessionId) {
		debugLog(`🚪 handlePeerLeave called for sessionId: ${sessionId}`);
		
		const pc = this.peerConnections.get(sessionId);
		if (pc) {
			debugLog(`   Closing peer connection for ${sessionId}`);
			// Completely close the connection and remove all event listeners
			pc.onicecandidate = null;
			pc.ontrack = null;
			pc.onconnectionstatechange = null;
			pc.oniceconnectionstatechange = null;
			pc.onicegatheringstatechange = null;
			pc.onsignalingstatechange = null;
			pc.onnegotiationneeded = null;
			pc.close();
			this.peerConnections.delete(sessionId);
		}
		
		// Also check if we have it by userId
		const userId = sessionId.split('_')[0];
		const pcByUserId = this.peerConnections.get(userId);
		if (pcByUserId) {
			debugLog(`   Closing peer connection for userId ${userId}`);
			pcByUserId.onicecandidate = null;
			pcByUserId.ontrack = null;
			pcByUserId.onconnectionstatechange = null;
			pcByUserId.oniceconnectionstatechange = null;
			pcByUserId.onicegatheringstatechange = null;
			pcByUserId.onsignalingstatechange = null;
			pcByUserId.onnegotiationneeded = null;
			pcByUserId.close();
			this.peerConnections.delete(userId);
		}
		
		// Clean up grace timers and per-peer stream classification state
		this.clearConnectionGraceTimer(sessionId);
		this.clearConnectionGraceTimer(userId);
		this.peerStreamMeta.delete(userId);
		this.peerScreenStreamIds.delete(userId);
		this.peerCameraStreamIds.delete(userId);
		
		// Clean up any pending offers and ICE candidates from this peer
		this.pendingOffers.delete(sessionId);
		this.pendingOffers.delete(userId);
		this.pendingIceCandidates.delete(sessionId);
		this.pendingIceCandidates.delete(userId);
		
		debugLog(`   ✅ Cleanup complete for ${sessionId}`);
		
		if (this.onRemoteStreamRemovedCallback) {
			this.onRemoteStreamRemovedCallback(sessionId);
		}
	}

	/**
	 * Wire up a present-sync data channel: deliver parsed messages to the
	 * registered callback and announce channel-open so the presenter can push
	 * current state to late joiners.
	 */
	setupPresentChannel(pc, channel, peerId, userId) {
		pc._presentChannel = channel;
		
		channel.onopen = () => {
			debugLog(`🔗 present-sync channel open for peer ${peerId}`);
			if (this.onPresentChannelOpenCallback) {
				this.onPresentChannelOpenCallback(pc._remoteSessionId || peerId, userId);
			}
		};
		
		channel.onmessage = (event) => {
			if (!this.onPresentMessageCallback) {
				return;
			}
			let message = null;
			try {
				message = JSON.parse(event.data);
			} catch (error) {
				console.warn(`⚠️ Invalid present-sync message from ${peerId}:`, error && error.message);
			}
			if (message) {
				this.onPresentMessageCallback(pc._remoteSessionId || peerId, userId, message);
			}
		};
		
		channel.onerror = (event) => {
			debugLog(`⚠️ present-sync channel error for peer ${peerId}`, event);
		};
	}

	/**
	 * Broadcast a present-sync message to all connected peers.
	 * Returns the number of peers the message was sent to.
	 */
	sendPresentMessage(message) {
		const payload = JSON.stringify(message);
		let sentCount = 0;
		this.peerConnections.forEach((pc, peerId) => {
			const channel = pc._presentChannel;
			if (channel && channel.readyState === 'open') {
				try {
					channel.send(payload);
					sentCount++;
				} catch (error) {
					console.warn(`⚠️ Failed to send present-sync message to ${peerId}:`, error && error.message);
				}
			}
		});
		return sentCount;
	}

	/**
	 * Send a present-sync message to a single peer (by sessionId or userId).
	 * Used to push current presenting state to a late joiner.
	 */
	sendPresentMessageTo(peerId, message) {
		let pc = this.peerConnections.get(peerId);
		if (!pc) {
			// Fall back to a userId match (connection may be keyed by sessionId)
			this.peerConnections.forEach((candidate) => {
				if (!pc && candidate._userId === peerId) {
					pc = candidate;
				}
			});
		}
		
		const channel = pc ? pc._presentChannel : null;
		if (channel && channel.readyState === 'open') {
			try {
				channel.send(JSON.stringify(message));
				return true;
			} catch (error) {
				console.warn(`⚠️ Failed to send present-sync message to ${peerId}:`, error && error.message);
			}
		}
		return false;
	}

	async startScreenShare() {
		try {
			// Check if getDisplayMedia is supported
			if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
				const error = new Error('Screen sharing is not supported on this device/browser. Screen sharing is not available on iOS Safari.');
				error.name = 'NotSupportedError';
				throw error;
			}

			const screenConstraints = {
				video: {
					displaySurface: 'window'
				},
				audio: false
			};
			
			this.screenStream = await navigator.mediaDevices.getDisplayMedia(screenConstraints);
			
			// ADD screen track to peer connections (don't replace camera)
			const screenTrack = this.screenStream.getVideoTracks()[0];
			
			// Set state BEFORE adding tracks so the renegotiation offers announce
			// the screen stream id in their streamMeta
			this.isScreenSharing = true;
			
			debugLog('🖥️ Adding screen track to peer connections (keeping camera active)');
			this.peerConnections.forEach((pc, peerId) => {
				try {
					// Add screen track as a second video track - this triggers
					// onnegotiationneeded, which sends the renegotiation offer
					pc.addTrack(screenTrack, this.screenStream);
					debugLog(`✅ Added screen track to peer ${peerId}`);
				} catch (error) {
					console.error(`❌ Error adding screen track to peer ${peerId}:`, error);
				}
			});
			
			// Handle screen share stop (when user clicks browser's "Stop Sharing" button)
			screenTrack.onended = () => {
				debugLog('🛑 Screen share ended by browser (user clicked Stop Sharing button)');
				this.stopScreenShare();
			};
			
			return this.screenStream;
		} catch (error) {
			this.isScreenSharing = false;
			console.error('Error starting screen share:', error);
			throw error;
		}
	}

	async stopScreenShare() {
		if (this.screenStream) {
			const screenTrack = this.screenStream.getVideoTracks()[0];
			
			// Clear state BEFORE removing tracks so the renegotiation offers
			// announce that the screen share has ended (streamMeta.screenStreamId = null)
			this.isScreenSharing = false;
			
			debugLog('🖥️ Removing screen track from peer connections (keeping camera)');
			this.peerConnections.forEach((pc, peerId) => {
				try {
					const screenSender = pc.getSenders().find(s => s.track === screenTrack);
					if (screenSender) {
						// removeTrack triggers onnegotiationneeded - perfect
						// negotiation handles the renegotiation, even for listeners
						pc.removeTrack(screenSender);
						debugLog(`Removed screen track from peer ${peerId}`);
					}
				} catch (error) {
					console.error(`❌ Error removing screen track from peer ${peerId}:`, error);
				}
			});
			
			// Stop the screen stream
			this.screenStream.getTracks().forEach(track => track.stop());
			this.screenStream = null;
		} else {
			this.isScreenSharing = false;
		}
		
		// Notify the UI component that screen sharing has stopped
		if (this.onScreenShareStoppedCallback) {
			this.onScreenShareStoppedCallback();
		}
	}

	toggleAudio(enabled) {
		if (this.localStream) {
			const audioTracks = this.localStream.getAudioTracks();
			audioTracks.forEach(track => {
				track.enabled = enabled;
			});
		}
	}

	toggleVideo(enabled) {
		if (this.localStream) {
			const videoTracks = this.localStream.getVideoTracks();
			videoTracks.forEach(track => {
				track.enabled = enabled;
			});
		}
	}

	async cleanup() {
		//console.log('🧹 Cleaning up WebRTC service...');
		
		// Stop presence heartbeat FIRST to prevent race conditions
		this.stopPresenceHeartbeat();
		
		// Store IDs before clearing them (in case intervals were already queued)
		const finalVaultId = this.vaultId;
		const finalSessionId = this.sessionId;
		const finalUserId = this.userId;
		
		// Clear IDs immediately to prevent any queued callbacks from using them
		this.vaultId = null;
		this.sessionId = null;
		this.userId = null;
		
		// Send final offline presence update with stored IDs
		if (finalVaultId && finalSessionId) {
			try {
				await this.updatePresenceWithIds(finalVaultId, finalSessionId, 'offline');
			} catch (error) {
				console.error('Error sending final presence update:', error);
			}
		}

		// Unsubscribe from signals
		if (this.signalSubscription) {
			this.signalSubscription.unsubscribe();
			this.signalSubscription = null;
		}

		// Notify peers we're leaving
		const peerIds = Array.from(this.peerConnections.keys());
		if (peerIds.length > 0) {
			console.log(`📤 Notifying ${peerIds.length} peer(s) we're leaving...`);
		}
		// Get unique user IDs from peer connections (some might be userId, some sessionId)
		const uniqueUserIds = new Set();
		for (const peerId of peerIds) {
			const userId = peerId.includes('_') ? peerId.split('_')[0] : peerId;
			uniqueUserIds.add(userId);
		}
		
		// Only send leave signals if we still have valid IDs
		if (finalVaultId && finalUserId) {
			for (const userId of uniqueUserIds) {
				try {
					await this.sendSignalWithIds(finalVaultId, finalSessionId, finalUserId, userId, 'leave', {});
				} catch (error) {
					console.error(`Error sending leave signal to ${userId}:`, error);
				}
			}
		}

	// Close all peer connections
	if (this.peerConnections.size > 0) {
		debugLog(`🔌 Closing ${this.peerConnections.size} peer connection(s)...`);
		this.peerConnections.forEach((pc) => {
			pc.close();
		});
		this.peerConnections.clear();
	}
	
	// Clear grace timers, per-peer state, and reconnection cooldowns
	this.connectionGraceTimers.forEach((timer) => clearTimeout(timer));
	this.connectionGraceTimers.clear();
	this.peerStreamMeta.clear();
	this.peerScreenStreamIds.clear();
	this.peerCameraStreamIds.clear();
	this.pendingOffers.clear();
	this.pendingIceCandidates.clear();
	this.reconnectionCooldown.clear();
	this.isLocalStreamReady = false;
	this.isScreenSharing = false;
	this.onPresentMessageCallback = null;
	this.onPresentChannelOpenCallback = null;

		// Stop local streams
		if (this.localStream) {
			console.log('🎥 Stopping local media stream...');
			this.localStream.getTracks().forEach(track => track.stop());
			this.localStream = null;
		}

		if (this.screenStream) {
			console.log('🖥️ Stopping screen share stream...');
			this.screenStream.getTracks().forEach(track => track.stop());
			this.screenStream = null;
		}

		//console.log('✅ WebRTC cleanup complete');
	}

	// Get the current session ID
	getSessionId() {
		return this.sessionId;
	}

	// Diagnostic method to check connection status
	getConnectionStats() {
		const stats = {
			sessionId: this.sessionId,
			userId: this.userId,
			turnActive: this.turnActive,
			totalPeers: this.peerConnections.size,
			peers: []
		};

		this.peerConnections.forEach((pc, peerId) => {
			stats.peers.push({
				peerId: peerId,
				connectionState: pc.connectionState,
				iceConnectionState: pc.iceConnectionState,
				iceGatheringState: pc.iceGatheringState,
				signalingState: pc.signalingState
			});
		});

		return stats;
	}
}

export default new WebRTCService();


